import { isEqual, last } from '@utils/helpers';
import * as D3Format from 'd3-format';
import * as D3Time from 'd3-time';
import * as moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFragment } from 'react-relay';
import { DatePresets, type XDomain, type YRange, getAllApplyingDatePresets, timeFormatter } from '../../widgets/performanceOverview';
import { ChartSliderWrapper } from './lib/chartSliderWrapper';
import { Datepicker } from './lib/datepicker';
const { LineChart } = require('kaidoo-chart/basic');

import { isNumber } from 'util';
import { performanceOverviewChartFragment } from './PerformanceOverviewChart.relay';
import type { PerformanceOverviewChartPropsType } from './types';

const yAxisFormatFunc = (d) => {
  const numberFormat = D3Format.format('.2f');
  let dFormatted = numberFormat(d);

  if (dFormatted === numberFormat(1)) {
    dFormatted = numberFormat(0);
  }
  return `${dFormatted}%`;
};

const selectPreset = (applyingDatePresets: DatePresets[], detailXDomain: XDomain, xDomain: XDomain): DatePresets => {
  if (applyingDatePresets.indexOf(DatePresets.TenYears) < 0) {
    return last(applyingDatePresets);
  }

  if (isEqual(moment(xDomain[1]).subtract(5, 'years').toDate(), xDomain[0])) {
    return DatePresets.TenYears;
  }

  return last(applyingDatePresets);
};

const PerformanceOverviewChart: React.FC<PerformanceOverviewChartPropsType> = (props) => {
  const chart = useFragment(performanceOverviewChartFragment, props.chart);

  const datePickerDomRef = useRef<HTMLDivElement>(null);

  const initialApplyingDatePresets = getAllApplyingDatePresets(props.xDomain, props.detailXDomain[0], props.detailXDomain[1]);

  const [isDatePickerEnabled, setIsDatePickerEnabled] = useState(false);
  const [applyingDatePresets, setApplyingDatePresets] = useState<DatePresets[]>(initialApplyingDatePresets);
  const [lineChartWidth, setLineChartWidth] = useState(0);
  const [lineChartXRange, setLineChartXRange] = useState<[number, number]>([0, 0]);
  const [detailXDomainState, setDetailXDomainState] = useState<XDomain | null>(null);

  const setChartWidth = useCallback(() => {
    if (!datePickerDomRef.current) {
      return;
    }

    const w = datePickerDomRef.current.offsetWidth;
    setLineChartWidth(props.width - w);
    setLineChartXRange([5, props.xRange[1] - w - 1]);
  }, [props.width, props.xRange]);

  useEffect(() => {
    setChartWidth();
  }, [setChartWidth]);

  useEffect(() => {
    if (props.width) {
      setChartWidth();
    }
  }, [props.width, setChartWidth]);

  const updateDetailXDomainHandler = useCallback(
    (detailXDomain: XDomain, newApplyingDatePresets: DatePresets[], newIsDatePickerEnabled: boolean): void => {
      props.updateDetailData(detailXDomain);

      setIsDatePickerEnabled(newIsDatePickerEnabled);
      setApplyingDatePresets(newApplyingDatePresets);
      setDetailXDomainState(detailXDomain);
    },
    [props.updateDetailData],
  );

  const enableDatepicker = useCallback(() => {
    setIsDatePickerEnabled(true);
  }, []);

  const closeDatePicker = useCallback(() => {
    setIsDatePickerEnabled(false);
  }, []);

  const { data, xDomain, yDomain, height, margins } = props;

  const detailXDomain = detailXDomainState || props.detailXDomain;

  const yRange = [height - margins.top - margins.bottom, 0] as YRange;

  const lineChartMargins = {
    ...margins,
    left: 0,
  };

  const x = (d) => d.date;

  const yGutmannPlus = (d) => (d >= 1 ? d : d >= -1 ? 1 : 0);
  const yGutmannMinus = (d) => (d <= -1 ? Math.abs(d) : d <= 1 ? 1 : 0);

  const yDomainPlus = [Math.max(1, yDomain[0]), Math.max(1, yDomain[1])];
  const yDomainMinus = [Math.abs(Math.min(1, yDomain[0])), Math.abs(Math.min(1, yDomain[1]))];

  const yRangePlusRatio = yDomain[1] - yDomain[0] !== 0 ? (yDomainPlus[1] - yDomainPlus[0]) / (yDomain[1] - yDomain[0]) : 1;
  const yRangePlus = [(height - margins.top - margins.bottom) * yRangePlusRatio, 0];
  const yRangeMinus = [height - margins.top - margins.bottom, (height - margins.top - margins.bottom) * yRangePlusRatio];

  const svgClassName = 'svg';

  const legendConfig = {
    show: false,
    backgroundColor: '#FFF',
    legendHeight: 50,
    legendPosition: 'left',
    legendOffset: 90,
    legendClassName: 'react-d3-core__legend',
    swatchShape: 'square',
  };

  const dataSeriesConfig = [
    {
      field: 'performance',
      name: 'Performance',
      className: 'performance',
    },
  ];

  const xTickValues = (xScale) => [...D3Time.timeMonths(xDomain[0], xDomain[1], 12), xDomain[1]];

  const xScaleSetConfig = [
    {
      scale: 'time',
      valueFunc: x,
      domain: xDomain,
      range: lineChartXRange,
      axis: {
        show: false,
        orient: 'bottom',
        className: 'xAxis',
        hShift: 0,
        vShift: 40,
        label: {
          show: false,
          title: '',
          position: 'top',
          offset: 0,
          hShift: 0,
          vShift: 0,
          rotate: 0,
          customStyle: null,
          className: '',
        },
        ticks: {
          sizeInner: 5,
          sizeOuter: 5,
          values: xTickValues,
          format: timeFormatter.bind(null, props.currentLanguage),
          count: null,
          padding: 5,
          orient: 'top',
          customTickStyle: null,
          tickClassName: 'xAxisTicks',
          customTickLabelStyle: null,
          tickLabelClassName: 'xAxisTickLabel',
        },
      },
      grid: {
        show: false,
        borderExcess: [0, 0],
        shift: 0,
        className: 'xGridLine',
      },
    },
  ];

  const borderExcess = [2, 2];
  const yGridDashFunction = (xScale, yScale) =>
    xTickValues(xScale)
      .map((date, i) => {
        const dashLength = 4;
        const xTicks = xTickValues(xScale);

        if (i === 0) {
          return [0, xScale(date) - xScale(xDomain[0]) - dashLength * 0.5 + borderExcess[0], dashLength];
        }

        if (i === xTicks.length - 1) {
          return [xScale(date) - xScale(xTicks[i - 1]) - dashLength, dashLength, margins.right];
        }

        return [xScale(date) - xScale(xTicks[i - 1]) - dashLength, dashLength];
      })
      .reduce((a, b) => {
        return a.concat(b);
      }, [])
      .toString();

  const customStyleFunc = (xScale, yScale) => ({
    strokeDasharray: yGridDashFunction(xScale, yScale),
  });

  const yScaleSetConfig = [
    {
      scale: 'log',
      base: 10,
      valueFunc: yGutmannPlus,
      domain: yDomainPlus,
      range: yRangePlus,
      axis: {
        show: false,
        orient: 'right',
        customStyle: null,
        className: 'yAxis',
        hShift: 0,
        vShift: 0,
        label: {
          show: false,
          title: '',
          position: 'right',
          offset: 0,
          hShift: 0,
          vShift: 0,
          rotate: 0,
          customStyle: null,
          className: '',
        },
        ticks: {
          sizeInner: 15.0,
          sizeOuter: 0.0,
          values: [],
          format: yAxisFormatFunc,
          count: null,
          padding: 10,
          orient: 'left',
          customTickStyle: null,
          tickClassName: 'yAxisTicks',
          customTickLabelStyle: null,
          tickLabelClassName: 'yAxisTickLabel',
        },
      },
      grid: {
        show: false,
        borderExcess,
        shift: 0,
        className: 'yGridLine',
        customStyle: customStyleFunc,
      },
    },
    {
      scale: 'log',
      base: 10,
      valueFunc: yGutmannMinus,
      domain: yDomainMinus,
      range: yRangeMinus,
      axis: {
        show: false,
        orient: 'right',
        className: 'yAxis',
        customStyle: null,
        hShift: 80,
        vShift: 0,
        label: {
          show: true,
          title: '',
          position: 'right',
          offset: 0,
          hShift: 0,
          vShift: 0,
          rotate: 0,
          customStyle: null,
          className: '',
        },
        ticks: {
          sizeInner: 3.0,
          sizeOuter: 0.0,
          values: [],
          format: yAxisFormatFunc,
          count: null,
          padding: 5,
          orient: 'right',
          customTickStyle: null,
          tickClassName: 'yAxisTicks',
          customTickLabelStyle: null,
          tickLabelClassName: 'yAxisTickLabel',
        },
      },
      grid: {
        show: false,
        borderExcess,
        shift: 0,
        className: 'yGridLine',
        customStyle: customStyleFunc,
      },
    },
  ];

  const datepickerButtonTexts = chart ? JSON.parse(chart.datepickerbuttontexts) : [];

  const largestSelectedDatePreset = isNumber(last(applyingDatePresets)) ? selectPreset(applyingDatePresets, detailXDomain, xDomain) : null;

  return (
    <div className="performanceOverviewSliderWrapper max-[700px]:mt-[60px] max-[700px]:h-12 max-[700px]:w-full max-[700px]:bg-[#f3f3f3]">
      {isDatePickerEnabled ? (
        <Datepicker
          xDomain={xDomain}
          detailXDomain={detailXDomain}
          updateDetailXDomainHandler={updateDetailXDomainHandler}
          closeDatePicker={closeDatePicker}
          timeFormatter={timeFormatter.bind(null, props.currentLanguage)}
          datePickerWrapperClassName="datePickerWrapper"
          text={chart.datepicker}
          applyingDatePresets={applyingDatePresets}
          location={props.location}
        />
      ) : null}
      <div className="datePickerButton table h-12 w-1/5 cursor-pointer bg-[#f3f3f3] text-[#668272] float-left max-[400px]:w-[35%]" onClick={enableDatepicker} ref={datePickerDomRef}>
        <div className="table-cell w-4/5 align-middle pl-[5%] text-left text-[initial]">{isNumber(largestSelectedDatePreset) ? datepickerButtonTexts[largestSelectedDatePreset] : 'Datum'}</div>
        <div className="dots table-cell w-1/5 align-middle bg-[url(/static/front/assets/performance-chart/punktis_blau.svg)] bg-position-[center_center] bg-no-repeat bg-size-[auto_15px]" />
      </div>
      <div className='performanceOverviewChart relative inline-block h-[13%] w-4/5 float-left before:invisible before:absolute before:block before:h-px before:w-px before:bg-white before:content-[""] before:mix-blend-overlay max-[700px]:hidden [&_g_text]:translate-y-[-3px] [&_g_text]:font-[Arial] [&_g_text]:text-sm [&_g_text]:leading-[22px] [&_g_text]:text-black [&_.xGridLine]:stroke-black [&_.xGridLine]:stroke-0! [&_.yGridLine]:stroke-black [&_.yGridLine]:stroke-[1px]! [&_.yGridLine:first-of-type]:[stroke-dasharray:1,2,1] [&_.xAxis_path]:stroke-0! [&_.xAxisTickLabel]:fill-black! [&_.xAxisTicks]:stroke-black! [&_.yAxis_path]:stroke-[1px]! [&_.yAxisTickLabel]:fill-black [&_.yAxisTicks]:stroke-black! [&_path.performance]:stroke-[#668272]'>
        <LineChart
          width={lineChartWidth}
          height={height}
          data={data}
          dataSeriesConfig={dataSeriesConfig}
          margins={lineChartMargins}
          svgClassName={svgClassName}
          xScaleSetConfig={xScaleSetConfig}
          yScaleSetConfig={yScaleSetConfig}
          legendConfig={legendConfig}
        >
          <ChartSliderWrapper
            updateDetailXDomainHandler={updateDetailXDomainHandler}
            xDomain={xDomain}
            detailXDomain={detailXDomain || xDomain}
            xRange={lineChartXRange}
            yRange={yRange}
            timeFormatter={timeFormatter.bind(null, props.currentLanguage)}
            sliderWrapperClassName="sliderWrapper"
          />
        </LineChart>
      </div>
    </div>
  );
};

export { PerformanceOverviewChart };
